import React from 'react';
import {
  GenderEnum,
  GetCoachesByGeoQuery,
  LessonSkillCategoriesEnum,
  SportsEnum,
} from 'types/generated/client';
import { GetCityCoachesQuery } from 'types/generated/server';
import { LessonPageModeEnum } from 'screens/ExploreLessons/enums';
import { SortByEnum } from 'screens/ExploreRemote/enums';
// Type-only: `constants` imports `CertificationEnum` from here, so a value import
// would close a runtime cycle. Types are erased, so this direction is safe.
import type { CoachAvailabilityFilter } from './constants';

export type CoachType = (
  | GetCoachesByGeoQuery['userProfiles'][0]
  | GetCoachesByGeoQuery['coachStarterProfiles'][0]
) & { distance: number };

export interface LessonListProps {
  instantBookEligibleCoaches: CoachType[];
  nonInstantBookEligibleCoaches: CoachType[];
  allCoaches: CoachType[]; // Original sorted coaches for non-EXPLORE modes
  loading: boolean;
  listHoveredCoachId: string | null;
  setListHoveredCoachId: React.Dispatch<React.SetStateAction<string | null>>;
  activeSport: SportsEnum;
  pageMode: LessonPageModeEnum;
  onClearFilters?: () => void;
  vairProMap: Record<string, boolean>;
}

export interface LessonMapMarkerProps {
  listHoveredCoachId: string | null;
  setListHoveredCoachId: React.Dispatch<React.SetStateAction<string | null>>;
  setActiveCoachId: React.Dispatch<React.SetStateAction<string | null>>;
  activeCoach: CoachType | undefined;
  coach: CoachType;
}

export interface LessonFiltersProps {
  setPriceMinimum: React.Dispatch<React.SetStateAction<number>>;
  priceMinimum: number;
  setPriceMaximum: React.Dispatch<React.SetStateAction<number>>;
  priceMaximum: number;
  setSelectedGender: React.Dispatch<React.SetStateAction<GenderEnum | null>>;
  selectedGender: GenderEnum | null;
  /** A `WorksWithOption.id` — spans age groups and skill levels. `null` is no filter. */
  setSelectedWorksWith: React.Dispatch<React.SetStateAction<string | null>>;
  selectedWorksWith: string | null;
  selectedCertification: CertificationEnum | null;
  setSelectedCertification: React.Dispatch<React.SetStateAction<CertificationEnum | null>>;
  distance: {
    name: string;
    id: number;
  };
  setDistance: (distance: { name: string; id: number }) => void;
  sortBy: SortByEnum;
  setSortBy: React.Dispatch<React.SetStateAction<SortByEnum>>;
  selectedSport: SportsEnum;
  pageMode: LessonPageModeEnum;
  /** Mobile-only "All / Instant book" tab — desktop splits the list instead. */
  availability: CoachAvailabilityFilter;
  setAvailability: React.Dispatch<React.SetStateAction<CoachAvailabilityFilter>>;
}

export enum CertificationEnum {
  PPR_CERTIFIED = 'PPR Certified',
  PTR_CERTIFIED = 'PTR Certified',
  PCR_CERTIFIED = 'PCR Certified',
  PCI_CERTIFIED = 'PCI Certified',
  VAIR_PRO = 'VAIR PRO',
  CARDIO_TENNIS = 'Cardio Tennis',
}

export interface SportsDropdownProps {
  selectedSport: SportsEnum | LessonSkillCategoriesEnum;
  handleSportChange: (sport: SportsEnum) => void;
  readonly: boolean;
  loading?: boolean;
  width?: string;
  height?: string;
}

export interface UseLessonProps {
  pageMode?: LessonPageModeEnum;
  initialSport?: SportsEnum;
  initialCityCoordinates?: {
    latitude: number;
    longitude: number;
  };
  serverCoachData?: {
    coaches: GetCityCoachesQuery['userProfiles'];
  };
}
